/**
 * <ymal-tabs> — tab switching for the "You may also like" section, plus
 * on-demand loading of the tabs that aren't open.
 *
 * The parent section renders exactly one grid: whichever tab starts active. The
 * others arrive from sections/you-may-also-like-tab.liquid, fetched as
 * /collections/<handle>?section_id=you-may-also-like-tab.
 *
 * That response knows nothing about the product being viewed — deliberately,
 * because that is what makes it identical for every visitor and every product
 * page, and therefore cacheable. Which is why removing the currently-viewed
 * product from a fetched tab is this file's job, not Liquid's.
 */
import { fetchCache } from '@theme/utilities';
import { localeUrl } from '@theme/global';

const SECTION = 'you-may-also-like-tab';

class YmalTabs extends HTMLElement {
  connectedCallback() {
    // The host is display:contents, so it has no box of its own — scrollIntoView
    // on `this` would silently do nothing. Everything geometric uses the child.
    this.root = this.firstElementChild;
    this.handle = this.dataset.currentHandle || '';
    this.buttons = [...this.querySelectorAll('.ymal-tab-btn')];

    this.buttons.forEach((btn) => {
      btn.addEventListener('click', () => this.select(btn));
      // Warm the cache before the click lands. fetchCache dedupes, so the click
      // that follows costs nothing and the panel is usually already filled.
      btn.addEventListener('pointerenter', () => this.load(this.panelFor(btn)), { once: true, passive: true });
      btn.addEventListener('focus', () => this.load(this.panelFor(btn)), { once: true });
    });

    // Same trigger the theme already uses for product-recommendations and
    // product-recently (custom.js:799, :860): warm once the block is nearly in
    // view, so the first click is instant. Costs two requests for someone who
    // scrolls past without clicking — both shared across every product page.
    new IntersectionObserver((entries, obs) => {
      if (!entries[0].isIntersecting) return;
      obs.disconnect();
      this.querySelectorAll('.ymal-panel[data-collection]').forEach((panel) => this.load(panel));
    }, { rootMargin: '0px 0px 600px 0px' }).observe(this.root);
  }

  panelFor(btn) {
    return document.getElementById(btn.getAttribute('data-ymal-tab'));
  }

  select(btn) {
    this.buttons.forEach((b) => b.setAttribute('aria-current', 'false'));
    btn.setAttribute('aria-current', 'true');
    this.querySelectorAll('.ymal-panel').forEach((p) => p.classList.remove('is-active'));

    const panel = this.panelFor(btn);
    if (panel) {
      panel.classList.add('is-active');
      this.load(panel);
    }
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async load(panel) {
    // No data-collection means this panel was rendered server-side; a state means
    // it is already loading, loaded, or failed. Either way there is nothing to do.
    if (!panel || !panel.dataset.collection || panel.dataset.state) return;

    panel.dataset.state = 'loading';
    const handle = panel.dataset.collection;
    const url = `${Shopify.routes.root}collections/${handle}?section_id=${SECTION}`;

    try {
      const res = await fetchCache(url, 'ymal');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const source = doc.querySelector('[data-ymal-cards]');

      // Keyed off the data attribute rather than the shopify-section- wrapper, the
      // same way custom.js:330 keys off offcanvas-compare — it makes this
      // indifferent to whether Shopify wraps a schema-less section.
      //
      // No cards is a failure, not an empty success: a smart collection's
      // membership index can report a count for products it has not finished
      // indexing, and rakhi-detect's own header documents that happening here.
      if (!source || !source.children.length) throw new Error('no cards');

      this.strip(source);
      panel.replaceChildren(...document.importNode(source, true).childNodes);
      panel.dataset.state = 'ready';
      delete panel.dataset.collection;

      // Rehydration, in full. Custom elements upgrade on insertion by themselves,
      // and Wishlist.connectedCallback (custom.js:127) resolves saved state from
      // the module-level list, so hearts come back already filled with no help
      // from us. localeUrl is a no-op while Shopify.routes.root is '/', and
      // currency:update has no listener while currency_type is '1' — both are one
      // line, both are what LazyModal does, and both start mattering the day a
      // locale or a currency switcher is turned on.
      //
      // LazyModal's other steps are deliberately absent: a product card has no rte
      // table, no target=_blank link, no payment button and no update-hidden badge.
      localeUrl(panel);
      document.dispatchEvent(new CustomEvent('currency:update'));
      panel.dispatchEvent(new CustomEvent('products:update', { bubbles: true }));
    } catch (e) {
      // Terminal on purpose. A tab that silently retries on every click is worse
      // than one that hands over a working link, and the collection page is a
      // strictly better destination than an empty grid.
      panel.dataset.state = 'error';
      const link = document.createElement('a');
      link.className = 'ymal-fallback';
      link.href = `${Shopify.routes.root}collections/${handle}`;
      link.textContent = panel.dataset.fallbackText || 'View all';
      panel.replaceChildren(link);
    }
  }

  /**
   * Drop the product being viewed from a fetched grid, before it is inserted.
   *
   * Matches on the handle in the card link rather than hdt-wishlist[data-id]: the
   * link at card-product1.liquid:60 is unconditional, whereas the wishlist button
   * disappears entirely when settings.wishlist_mode is '0' — one store setting
   * away from silently breaking this.
   */
  strip(source) {
    if (!this.handle) return;
    source.querySelectorAll('hdt-card-product').forEach((card) => {
      const link = card.querySelector('a[data-pr-url][href]');
      if (!link) return;
      // Tolerates /products/x, /en/products/x and /collections/c/products/x alike.
      const handle = link.getAttribute('href').split('?')[0].split('/products/')[1];
      if (handle === this.handle) card.remove();
    });
  }
}

customElements.define('ymal-tabs', YmalTabs);
